import React, { useState, useEffect, useCallback } from "react";
import {
  Spinner,
  Button,
  Badge,
  Form,
  Alert,
  Modal,
  Pagination,
} from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
  faRotate,
  faFileExcel,
  faTrash,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";

const SOURCE_LABEL = {
  MANUAL: { text: "Manual", bg: "secondary" },
  MAINTENANCE: { text: "Maintenance", bg: "info" },
  KAIZEN: { text: "Kaizen", bg: "warning" },
};
const STATUS_LABEL = {
  PENDING: { text: "Menunggu", bg: "warning" },
  APPROVED: { text: "Disetujui", bg: "success" },
  REJECTED: { text: "Ditolak", bg: "danger" },
};

// Bangun daftar nomor halaman dengan elipsis: 1 … 4 5 6 … 20
function getPageWindow(current, total) {
  const delta = 1;
  const range = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const out = [];
  let prev;
  for (const i of range) {
    if (prev) {
      if (i - prev === 2) out.push(prev + 1);
      else if (i - prev !== 1) out.push("...");
    }
    out.push(i);
    prev = i;
  }
  return out;
}

const StockApprovalTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("ALL");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  // --- Pagination (per halaman) ---
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- Edit permintaan (hanya PENDING) ---
  const [editRow, setEditRow] = useState(null); // baris yang sedang diedit
  const [items, setItems] = useState([]); // daftar sparepart untuk dropdown
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/stock-requests?status=${status}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      setError("");
    } catch (err) {
      console.error("❌ fetch stock-requests:", err);
      setError("Gagal mengambil data permintaan stock");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Kembali ke halaman 1 saat filter status berubah
  useEffect(() => {
    setPage(1);
  }, [status]);

  // Jaga halaman tetap valid saat jumlah data / ukuran halaman berubah
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIdx = (page - 1) * pageSize;
  const pageRows = rows.slice(startIdx, startIdx + pageSize);

  const approve = async (r) => {
    if (Number(r.stock_tersedia) < Number(r.qty)) {
      const ok = await popup.confirm(
        `Stok tersedia (${r.stock_tersedia}) kurang dari diminta (${r.qty}). Tetap setujui? (akan gagal bila stok tidak cukup)`,
      );
      if (!ok) return;
    } else {
      const ok = await popup.confirm(
        `Setujui pengeluaran ${r.qty} ${r.part_name}?`,
      );
      if (!ok) return;
    }
    try {
      setBusyId(r.id);
      await api.post(`/stock-requests/${r.id}/approve`);
      await fetchData();
      popup.success("Permintaan disetujui");
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menyetujui permintaan");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (r) => {
    const reason = window.prompt("Alasan penolakan (opsional):", "");
    if (reason === null) return; // batal
    try {
      setBusyId(r.id);
      await api.post(`/stock-requests/${r.id}/reject`, { reason });
      await fetchData();
      popup.success("Permintaan ditolak");
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menolak permintaan");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    const ok = await popup.confirm(
      `Hapus permintaan ${r.qty} ${r.part_name}? Data ini akan hilang permanen.`,
    );
    if (!ok) return;
    try {
      setBusyId(r.id);
      await api.delete(`/stock-requests/${r.id}`);
      await fetchData();
      popup.success("Data berhasil dihapus");
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal menghapus permintaan");
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = async (r) => {
    setEditRow(r);
    setEditItemId(r.stock_item_id);
    setEditQty(String(r.qty ?? ""));
    setEditNote(r.note || "");
    if (!itemsLoaded) {
      try {
        const res = await api.get("/stock-items");
        setItems(Array.isArray(res.data) ? res.data : []);
        setItemsLoaded(true);
      } catch (err) {
        console.error("❌ fetch stock-items:", err);
        popup.alert("Gagal mengambil daftar sparepart");
      }
    }
  };

  const closeEdit = () => {
    if (saving) return;
    setEditRow(null);
  };

  const saveEdit = async () => {
    const qtyNum = Number(editQty);
    if (!editItemId) {
      popup.alert("Pilih sparepart terlebih dahulu");
      return;
    }
    if (!Number.isInteger(qtyNum) || qtyNum <= 0) {
      popup.alert("Qty harus berupa angka lebih dari 0");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/stock-requests/${editRow.id}`, {
        stock_item_id: editItemId,
        qty: qtyNum,
        note: editNote.trim() || null,
      });
      setEditRow(null);
      await fetchData();
      popup.success("Permintaan berhasil diperbarui");
    } catch (err) {
      popup.alert(err.response?.data?.message || "Gagal memperbarui permintaan");
    } finally {
      setSaving(false);
    }
  };

  const itemOptions = items.map((it) => ({
    value: it.id,
    label: `${it.part_name}${it.spesifikasi ? ` — ${it.spesifikasi}` : ""}${
      it.kode ? ` [${it.kode}]` : ""
    } · stok: ${it.stock} ${it.unit || ""}`.trim(),
  }));
  const selectedItemOption =
    itemOptions.find((o) => o.value === editItemId) || null;

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const konteks = (r) => {
    if (r.source === "MAINTENANCE") return r.machine_name || "-";
    if (r.source === "KAIZEN") return r.kaizen_mesin || "-";
    return "-";
  };

  const exportExcel = () => {
    if (rows.length === 0) {
      popup.alert("Data masih kosong");
      return;
    }
    const excelData = rows.map((r, i) => {
      const deducted = r.status === "APPROVED";
      const stockAwal = deducted
        ? Number(r.stock_tersedia) + Number(r.qty)
        : Number(r.stock_tersedia);
      const stockAktual =
        r.status === "REJECTED" ? stockAwal : stockAwal - Number(r.qty);
      return {
        No: i + 1,
        Tanggal: fmt(r.created_at),
        Pemohon: r.requester_name || "-",
        Sumber: SOURCE_LABEL[r.source]?.text || r.source,
        Mesin: konteks(r),
        Item: r.part_name,
        Spek: r.spesifikasi || "-",
        "Stock Awal": stockAwal,
        "Qty Minta": Number(r.qty),
        "Stock Aktual": stockAktual,
        Keterangan: r.note || "-",
        Status: STATUS_LABEL[r.status]?.text || r.status,
        "Alasan Tolak": r.reject_reason || "-",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Persetujuan Stock");
    XLSX.writeFile(workbook, `Persetujuan_Stock_${status}.xlsx`);
  };

  return (
    <div className="container-fluid p-2">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 style={{ margin: 0 }}>Persetujuan Pengeluaran Stock</h2>
        <div className="d-flex gap-2">
          <Form.Select
            size="sm"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: "170px" }}
          >
            <option value="PENDING">Menunggu</option>
            <option value="APPROVED">Disetujui</option>
            <option value="REJECTED">Ditolak</option>
            <option value="ALL">Semua</option>
          </Form.Select>
          <div className="d-flex align-items-center gap-1">
            <span className="text-muted small text-nowrap">Per halaman:</span>
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "80px" }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
          <Button size="sm" variant="outline-primary" onClick={fetchData}>
            <FontAwesomeIcon icon={faRotate} /> Refresh
          </Button>
          <Button
            size="sm"
            variant="success"
            onClick={exportExcel}
            disabled={rows.length === 0}
          >
            <FontAwesomeIcon icon={faFileExcel} /> Export Excel
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center p-5 text-muted">
          <h5>Tidak ada permintaan {STATUS_LABEL[status]?.text || (status === "ALL" ? "" : status)}</h5>
        </div>
      ) : (
        <>
        <div
          className="table-wrapper"
          style={{ width: "100%", overflowX: "auto", maxHeight: "78vh" }}
        >
          <table className="preview-table">
            <thead className="text-center">
              <tr>
                {[
                  "No",
                  "Tanggal",
                  "Pemohon",
                  "Sumber",
                  "Mesin",
                  "Item",
                  "Spek",
                  "Stock Awal",
                  "Qty Minta",
                  "Stock Aktual",
                  "Keterangan",
                  "Status",
                  "Aksi",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      background: "#1e3a8a",
                      color: "#fff",
                      ...(h === "Aksi" ? { minWidth: 180 } : {}),
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, i) => {
                const src = SOURCE_LABEL[r.source] || {
                  text: r.source,
                  bg: "secondary",
                };
                const st = STATUS_LABEL[r.status] || {
                  text: r.status,
                  bg: "secondary",
                };
                const deducted = r.status === "APPROVED";
                const stockAwal = deducted
                  ? Number(r.stock_tersedia) + Number(r.qty)
                  : Number(r.stock_tersedia);
                const stockAktual =
                  r.status === "REJECTED"
                    ? stockAwal
                    : stockAwal - Number(r.qty);
                return (
                  <tr key={r.id}>
                    <td className="text-center">{startIdx + i + 1}</td>
                    <td className="text-center">{fmt(r.created_at)}</td>
                    <td className="text-center">{r.requester_name || "-"}</td>
                    <td className="text-center">
                      <Badge bg={src.bg}>{src.text}</Badge>
                    </td>
                    <td className="text-center">{konteks(r)}</td>
                    <td className="cell-left">
                      <div className="fw-bold">{r.part_name}</div>
                    </td>
                    <td className="cell-left">{r.spesifikasi || "-"}</td>
                    <td className="text-center">{stockAwal}</td>
                    <td className="text-center fw-bold">{r.qty}</td>
                    <td
                      className="text-center fw-bold"
                      style={{ color: stockAktual < 0 ? "#dc3545" : "inherit" }}
                    >
                      {stockAktual}
                    </td>
                    <td className="cell-left">{r.note || "-"}</td>
                    <td className="text-center">
                      <Badge bg={st.bg}>{st.text}</Badge>
                      {r.status === "REJECTED" && r.reject_reason && (
                        <div>
                          <small className="text-muted">
                            {r.reject_reason}
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <div className="d-flex gap-1 justify-content-center flex-nowrap">
                        {r.status === "PENDING" && (
                          <>
                            <Button
                              size="sm"
                              variant="success"
                              disabled={busyId === r.id}
                              onClick={() => approve(r)}
                              title="Setujui"
                            >
                              {busyId === r.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <FontAwesomeIcon icon={faCheck} />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={busyId === r.id}
                              onClick={() => reject(r)}
                              title="Tolak"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </Button>
                          </>
                        )}
                        {(r.status === "PENDING" ||
                          r.status === "APPROVED") && (
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={busyId === r.id}
                            onClick={() => openEdit(r)}
                            title={
                              r.status === "APPROVED"
                                ? "Edit (stok akan disesuaikan)"
                                : "Edit"
                            }
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={busyId === r.id}
                          onClick={() => remove(r)}
                          title="Hapus"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
          <div className="text-muted small">
            Menampilkan {startIdx + 1}–{Math.min(startIdx + pageSize, rows.length)}{" "}
            dari {rows.length} data
          </div>
          <div className="d-flex align-items-center gap-2">
            {totalPages > 1 && (
              <Pagination size="sm" className="mb-0">
                <Pagination.First
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                />
                <Pagination.Prev
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                {getPageWindow(page, totalPages).map((p, idx) =>
                  p === "..." ? (
                    <Pagination.Ellipsis key={`e${idx}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={p}
                      active={p === page}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Pagination.Item>
                  ),
                )}
                <Pagination.Next
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
                <Pagination.Last
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                />
              </Pagination>
            )}
          </div>
        </div>
        </>
      )}

      <Modal show={!!editRow} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pengeluaran Sparepart</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRow && (
            <>
              <div className="mb-2 text-muted small">
                Pemohon: <b>{editRow.requester_name || "-"}</b> · Sumber:{" "}
                <b>{SOURCE_LABEL[editRow.source]?.text || editRow.source}</b>
              </div>
              {editRow.status === "APPROVED" && (
                <Alert variant="warning" className="py-2 small">
                  Permintaan ini <b>sudah disetujui</b>. Menyimpan perubahan akan{" "}
                  <b>menyesuaikan stok otomatis</b>: qty lama dikembalikan, lalu
                  qty baru dipotong (dicatat sebagai koreksi di riwayat stok).
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Sparepart</Form.Label>
                <Select
                  options={itemOptions}
                  value={selectedItemOption}
                  onChange={(opt) => setEditItemId(opt ? opt.value : null)}
                  placeholder={
                    itemsLoaded ? "Pilih sparepart..." : "Memuat daftar..."
                  }
                  isLoading={!itemsLoaded}
                  isSearchable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Qty</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={editQty}
                  onChange={(e) => setEditQty(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label>Keterangan</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="Opsional"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={saving}>
            Batal
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-1" />
                Menyimpan...
              </>
            ) : (
              "Simpan"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StockApprovalTab;
